import CloseIcon from '@mui/icons-material/Close';
import Search from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import LinearProgress from '@mui/material/LinearProgress';
import Popper from '@mui/material/Popper';
import { SxProps, Theme } from '@mui/material/styles';
import useAutocomplete from '@mui/material/useAutocomplete';
import {
	KeyboardEvent,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState
} from 'react';
import { useNavigate } from 'react-router';

import { TEST_IDS } from '@/core/constants/app';
import { paths } from '@/core/router/paths';
import { useSetPendingView } from '@/core/store/recentHistory/recentHistorySelectors';
import { mergeSx } from '@/utils/mergeSx';
import { scrollBackToTop } from '@/utils/scrollBackToTop';
import { generateQueryString } from '@/utils/url/generateQueryString';
import { useDebouncedValue } from '@/utils/useDebouncedValue';

import { useQuickSearch } from './_hooks/useQuickSearch';
import { useRecentHistory } from './_hooks/useRecentHistory';
import {
	isAdvancedSearch,
	isProfileVisit,
	isQuickSearchResult,
	isRecentSearch,
	QuickSearchOption
} from './_types/QuickSearch.types';
import { quickSearchStyles as sx } from './QuickSearch.styles';
import { QuickSearchResults } from './QuickSearchResults/QuickSearchResults';
import { RecentHistory } from './RecentHistory/RecentHistory';

const MINIMUM_CHARS_FOR_SEARCH = 0;

type Props = {
	value: string;
	onChange: (value: string) => void;
	placeholder?: string;
	autoFocus?: boolean;
	disableDropdownOnLoad?: boolean;
	handleClear?: () => void;
	handleSearch?: (value: string) => void;
	customSx?: {
		root?: SxProps<Theme>;
		searchRoot?: SxProps<Theme>;
		searchInput?: SxProps<Theme>;
		searchIcon?: SxProps<Theme>;
		clearIcon?: SxProps<Theme>;
		popper?: SxProps<Theme>;
		dropdown?: SxProps<Theme>;
	};
};

const getOptionLabel = (option: QuickSearchOption) => {
	if (isProfileVisit(option)) {
		return option.profile.veevaid_V;
	}

	if (isRecentSearch(option)) {
		return `${option.searchKey}-${option.entityType}`;
	}

	if (isQuickSearchResult(option)) {
		return option.entity.veevaid_V;
	}

	if (isAdvancedSearch(option)) {
		return 'AdvancedSearch';
	}

	return '';
};

export const QuickSearch = ({
	value,
	onChange,
	placeholder,
	autoFocus,
	disableDropdownOnLoad,
	handleClear,
	handleSearch,
	customSx
}: Props) => {
	const navigate = useNavigate();
	const setPendingView = useSetPendingView();
	const {
		loading: recentHistoryLoading,
		options: recentHistory,
		fetchRecentHistory
	} = useRecentHistory();
	const [allowOpenOnFocus, setAllowOpenOnFocus] = useState(
		!disableDropdownOnLoad
	);
	const [popperWidth, setPopperWidth] = useState(400);
	const optionSelectedRef = useRef(false);

	const query = useDebouncedValue(value.trim(), 150);
	const {
		options: quickSearchResults,
		loading: quickSearchLoading,
		handleSelection,
		updateResults,
		resetResults
	} = useQuickSearch();
	const isQuickSearchActive =
		quickSearchResults !== null && query.length > MINIMUM_CHARS_FOR_SEARCH;

	useEffect(() => {
		if (query) {
			updateResults(query);
		} else if (query === '') {
			resetResults();
		}
	}, [query, resetResults, updateResults]);

	useEffect(() => {
		fetchRecentHistory();
		// eslint-disable-next-line react-hooks/set-state-in-effect -- intentional post-mount enable to suppress dropdown flash on initial render
		setAllowOpenOnFocus(true);
	}, [fetchRecentHistory]);

	const options: QuickSearchOption[] = useMemo(
		() => [
			...(isQuickSearchActive ? quickSearchResults : []),
			...recentHistory,
			{
				__typename: 'AdvancedSearch'
			}
		],
		[isQuickSearchActive, quickSearchResults, recentHistory]
	);

	const {
		getRootProps,
		getInputProps,
		getListboxProps,
		getOptionProps,
		getClearProps,
		anchorEl,
		groupedOptions,
		popupOpen,
		setAnchorEl
	} = useAutocomplete<QuickSearchOption, false, false, true>({
		id: 'quick-search',
		options,
		autoComplete: false,
		clearOnBlur: false,
		blurOnSelect: true,
		openOnFocus: allowOpenOnFocus,
		inputValue: value,
		value: null,
		autoHighlight: false,
		handleHomeEndKeys: true,
		onInputChange: (_, newValue, reason) => {
			if (reason === 'input') {
				onChange(newValue);
			}

			if (reason === 'clear' && handleClear) {
				setPendingView(null);
				handleClear();
			}
		},
		freeSolo: true,
		onChange: (_, newValue, reason) => {
			if (typeof newValue === 'string') {
				if (reason === 'createOption' && newValue && handleSearch) {
					handleSearch(newValue);
				}
			} else if (reason === 'selectOption') {
				optionSelectedRef.current = true;
				if (isAdvancedSearch(newValue)) {
					navigate({
						pathname: paths.searchHcp,
						search:
							query &&
							generateQueryString({
								name: query
							})
					});
					scrollBackToTop();
				} else {
					handleSelection(newValue);
				}
			}
		},
		groupBy: opt => opt.__typename ?? '',
		getOptionLabel: opt =>
			typeof opt === 'string' ? opt : getOptionLabel(opt),
		filterOptions: options => options,
		onOpen: () => {
			if (!isQuickSearchActive) {
				fetchRecentHistory();
			}
		}
	});

	useLayoutEffect(() => {
		if (!anchorEl) return;

		const observer = new ResizeObserver(entries => {
			setPopperWidth(entries[0].borderBoxSize[0].inlineSize);
		});
		observer.observe(anchorEl);

		return () => {
			observer.disconnect();
		};
	}, [anchorEl]);

	const { color, ...clearProps } = getClearProps();

	const rootProps = getRootProps();
	const handleRootKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
		optionSelectedRef.current = false;
		rootProps.onKeyDown?.(event);
		if (
			event.key === 'Enter' &&
			!optionSelectedRef.current &&
			!event.defaultPrevented &&
			value.trim() === '' &&
			handleClear
		) {
			setPendingView(null);
			handleClear();
		}
	};

	return (
		<Box sx={customSx?.root}>
			<Box
				ref={setAnchorEl}
				{...rootProps}
				onKeyDown={handleRootKeyDown}
				sx={mergeSx(sx.searchRoot, customSx?.searchRoot)}
			>
				<InputBase
					sx={mergeSx(sx.searchInput, customSx?.searchInput)}
					placeholder={placeholder}
					fullWidth
					// eslint-disable-next-line jsx-a11y/no-autofocus
					autoFocus={autoFocus}
					inputProps={{
						...getInputProps(),
						'data-testid': TEST_IDS.search.quickSearchInput
					}}
					startAdornment={
						<Search sx={mergeSx(sx.searchIcon, customSx?.searchIcon)} />
					}
					endAdornment={
						handleClear &&
						value.length > 0 && (
							<IconButton
								sx={mergeSx(sx.clearIcon, customSx?.clearIcon)}
								aria-label='Clear search'
								{...clearProps}
							>
								<CloseIcon fontSize='small' />
							</IconButton>
						)
					}
				/>
			</Box>

			<Popper
				open={popupOpen && (isQuickSearchActive || options.length > 1)}
				anchorEl={anchorEl}
				placement='bottom'
				disablePortal
				modifiers={[
					{
						name: 'offset',
						options: {
							offset: [0, 4]
						}
					}
				]}
				sx={mergeSx(sx.popper, { width: popperWidth }, customSx?.popper)}
			>
				<Box
					component='ul'
					{...getListboxProps()}
					sx={mergeSx(sx.dropdown, customSx?.dropdown)}
				>
					{(quickSearchLoading || recentHistoryLoading) && (
						<LinearProgress sx={sx.linearProgress} />
					)}

					{isQuickSearchActive ? (
						<QuickSearchResults
							query={query}
							groupedOptions={groupedOptions}
							getOptionProps={getOptionProps}
							anchorEl={anchorEl}
						/>
					) : (
						<RecentHistory
							groupedOptions={groupedOptions}
							getOptionProps={getOptionProps}
						/>
					)}
				</Box>
			</Popper>
		</Box>
	);
};
